import { Reader } from '../bytes/reader';
import { ParseError } from '../bytes/errors';
import { concat } from '../bytes/encoding';
import { u8, u16, u24, u64, vec, vecOf, readVecOf } from '../tls/vectors';
import { CT_V1, LogEntryType, MerkleLeafType, type EntryType, type ExtraData, type MerkleTreeLeaf, type SignedEntry, type TimestampedEntry } from './types';

export function entryTypeFromCode(code: number): EntryType {
  if (code === LogEntryType.x509_entry) return 'x509';
  if (code === LogEntryType.precert_entry) return 'precert';
  throw new ParseError(`unknown LogEntryType ${code}`);
}
export const entryTypeCode = (t: EntryType): number => (t === 'x509' ? LogEntryType.x509_entry : LogEntryType.precert_entry);

/** Reads `signed_entry` (the LogEntryType-dependent part) from the current position. */
export function readSignedEntry(r: Reader, entryType: EntryType): SignedEntry {
  if (entryType === 'x509') return { type: 'x509', cert: r.vec(3) };
  const issuerKeyHash = r.bytes(32);
  const tbs = r.vec(3);
  return { type: 'precert', issuerKeyHash, tbs };
}

export function encodeSignedEntry(e: SignedEntry): Uint8Array {
  return e.type === 'x509' ? vec(3, e.cert) : concat(e.issuerKeyHash, vec(3, e.tbs));
}

export function readTimestampedEntry(r: Reader): TimestampedEntry {
  const timestamp = r.u64n();
  const entryType = entryTypeFromCode(r.u16());
  const signed = readSignedEntry(r, entryType);
  const extensions = r.vec(2);
  const e: TimestampedEntry = { timestamp, entryType, extensions };
  if (signed.type === 'x509') e.x509 = signed.cert;
  else e.precert = { issuerKeyHash: signed.issuerKeyHash, tbs: signed.tbs };
  return e;
}

export function signedEntryOf(e: TimestampedEntry): SignedEntry {
  if (e.entryType === 'x509') return { type: 'x509', cert: e.x509! };
  return { type: 'precert', issuerKeyHash: e.precert!.issuerKeyHash, tbs: e.precert!.tbs };
}

export function encodeTimestampedEntry(e: TimestampedEntry): Uint8Array {
  return concat(u64(e.timestamp), u16(entryTypeCode(e.entryType)), encodeSignedEntry(signedEntryOf(e)), vec(2, e.extensions));
}

export function parseMerkleTreeLeaf(raw: Uint8Array): MerkleTreeLeaf {
  const r = new Reader(raw);
  const version = r.u8();
  if (version !== CT_V1) throw new ParseError(`unsupported MerkleTreeLeaf version ${version}`, 0);
  const leafType = r.u8();
  if (leafType !== MerkleLeafType.timestamped_entry) throw new ParseError(`unsupported MerkleLeafType ${leafType}`, 1);
  const entry = readTimestampedEntry(r);
  r.expectEnd('MerkleTreeLeaf');
  return { version, leafType, entry, raw };
}

export function encodeMerkleTreeLeaf(entry: TimestampedEntry): Uint8Array {
  return concat(u8(CT_V1), u8(MerkleLeafType.timestamped_entry), encodeTimestampedEntry(entry));
}

/** Parses get-entries `extra_data` (RFC 6962 §4.6). Needs the entry type to know the layout. */
export function parseExtraData(raw: Uint8Array, entryType: EntryType): ExtraData {
  const r = new Reader(raw);
  const out: ExtraData = { chain: [] };
  if (entryType === 'precert') out.precert = r.vec(3);
  out.chain = readVecOf(r, 3, 3);
  r.expectEnd('extra_data');
  return out;
}

export function encodeExtraData(d: ExtraData, entryType: EntryType): Uint8Array {
  const chain = vecOf(3, 3, d.chain);
  return entryType === 'precert' ? concat(vec(3, d.precert ?? new Uint8Array(0)), chain) : chain;
}

// keep u24 referenced for symmetric API users
export { u24 };
